import { ResponseError } from "@msuite/utils/errors";
import { guardString } from "@msuite/utils/strings";
import type { Nullable } from "@msuite/utils/types";
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";
import {
	useInfiniteQuery as _useInfiniteQuery,
	useQuery as _useQuery,
	type InfiniteData,
	type Query,
	QueryClient,
	QueryClientProvider,
	QueryObserver,
	useIsFetching,
	useMutation,
	useQueryClient,
} from "@tanstack/react-query";
import { persistQueryClient } from "@tanstack/react-query-persist-client";
import { type PropsWithChildren, useEffect } from "react";

/**
 * The sanctioned per-query retry override. A bare numeric `retry` replaces the
 * status-aware default wholesale and silently reinstates retrying 4xx refusals,
 * so a call site that only wants a different attempt count passes
 * `retry: retryLimit(n)` instead.
 *
 * @param maxAttempts - Retries granted to network and 5xx failures.
 * @returns A retry predicate that never retries a 4xx verdict.
 */
export function retryLimit(maxAttempts: number) {
	return (failureCount: number, error: Error): boolean => {
		if (error instanceof ResponseError && error.status >= 400 && error.status < 500) return false;
		return failureCount < maxAttempts;
	};
}

const BASE_QUERY_OPTIONS = {
	staleTime: 6_000 * 10,
	experimental_prefetchInRender: true,
	refetchOnWindowFocus: true,
	// A 4xx is a verdict, not a hiccup: retrying a refusal turns one denial
	// into four requests and four toasts, so only network and 5xx errors retry.
	// A bare numeric `retry` on a query replaces this default wholesale and
	// reinstates 4xx retries — `retryLimit(n)` above is the sanctioned form for
	// a different attempt count.
	retry: retryLimit(3),
	retryDelay: 1000,
} as const;

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			...BASE_QUERY_OPTIONS,
			enabled: false,
		},
	},
});

const localStoragePersister = createSyncStoragePersister({
	storage: window.localStorage,
});

/**
 * Drop every cached query, in memory and in its `localStorage` copy. The client is a module
 * singleton whose keys do not carry the organisation, so whatever changes who the caller is — a
 * different account, a different active organisation — has to empty it before the next read, or
 * the previous identity's rows are served to the new one until they go stale. Call it right before
 * a hard navigation: the reload is what tears down the rest of the per-identity state.
 */
function resetQueryCache() {
	queryClient.clear();
	localStoragePersister.removeClient();
}

/** Props Interface */
interface QueryProviderProps extends PropsWithChildren {
	buster: Nullable<string>;
}

function QueryProvider({ children, buster }: QueryProviderProps) {
	useEffect(() => {
		queryClient.setDefaultOptions({
			queries: {
				...BASE_QUERY_OPTIONS,
				enabled: buster !== null,
			},
		});
	}, [buster]);

	useEffect(() => {
		persistQueryClient({
			queryClient,
			persister: localStoragePersister,
			maxAge: 1000 * 60 * 60 * 24, // 24 hours
			buster: guardString(buster),
		});
	}, [buster]);

	return <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>;
}

function useQuery<T>({
	queryKey: _queryKey,
	onSuccess,
	...options
}: Parameters<typeof _useQuery<T>>[0] & {
	queryKey: (string | null)[];
	onSuccess?: (data: T) => void;
}) {
	/** Hooks */
	const queryKey = useQueryKey(_queryKey);

	/** Return */
	const { refetch: _refetch, ...data } = _useQuery<T>({
		...options,
		queryKey,
	});

	/** Effects */
	useEffect(() => {
		if (data.isSuccess && onSuccess) {
			onSuccess(data.data);
		}
	}, [data.data]);

	/** Functions */
	function push(data: T extends Array<infer U> ? U : T) {
		queryClient.setQueryData(queryKey, (oldData: T) => {
			if (Array.isArray(oldData)) {
				return [...oldData, data];
			}
			return data;
		});
	}

	async function refetch() {
		await _refetch();
	}

	return { ...data, queryKey, push, refetch };
}

function useInfiniteQuery<TData, TPageParam>({
	queryKey: _queryKey,
	...options
}: Omit<
	Parameters<typeof _useInfiniteQuery<TData, Error, InfiniteData<TData>, string[], TPageParam>>[0],
	"queryKey"
> & {
	queryKey: (string | null)[];
}) {
	const queryKey = useQueryKey(_queryKey).filter((k): k is string => k !== null);
	const result = _useInfiniteQuery<TData, Error, InfiniteData<TData>, string[], TPageParam>({
		...options,
		queryKey,
	});
	return { ...result, queryKey };
}

type InvalidateKey = string | string[];

function invalidate(key: InvalidateKey) {
	return ({ queryKey }: Query) => {
		if (Array.isArray(key)) {
			return key.some((k) => {
				return queryKey.includes(k);
			});
		}
		return queryKey.includes(key);
	};
}

type QueryKey = (string | null)[];

export function useQueryKey(key: (string | null)[]) {
	const queryKey = ["organization", ...key];
	return queryKey;
}

export {
	invalidate,
	queryClient,
	QueryObserver,
	QueryProvider,
	resetQueryCache,
	useInfiniteQuery,
	useIsFetching,
	useMutation,
	useQuery,
	useQueryClient,
	type Query,
	type QueryClient,
	type QueryKey,
};

export type RefetchFn = ReturnType<typeof useQuery<unknown>>["refetch"];
